import { inject, Injectable, signal } from '@angular/core';
import { DialogService } from '@marmon/frontend/common';
import { ConfirmDialogComponent } from '@marmon/frontend/components';
import { ConfigService } from '@marmon/frontend/config';
import { LoggerService } from '@marmon/frontend/logging';
import {
	Analysis,
	ExpirationInstant,
	extendExpirationDate,
	isWithinExpirationExtensionWindow,
	positiveDayCount,
} from '@marmon/shared/types/analysis';
import { RemoteConfigNumberDefaults } from '@marmon/shared/types/config';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { Timestamp } from 'firebase/firestore';
import { defaultIfEmpty } from 'rxjs';
import { AnalysisApiService } from './analysis-api.service';

export type AnalysisExpirationSettings = {
	thresholdDays: number;
	extensionDays: number;
};

export function defaultAnalysisExpirationSettings(): AnalysisExpirationSettings {
	return {
		thresholdDays:
			RemoteConfigNumberDefaults.analysis_expiration_extension_threshold_days,
		extensionDays:
			RemoteConfigNumberDefaults.analysis_expiration_extension_days,
	};
}

/**
 * App-wide expiration extension. Threshold and duration come from Remote Config
 * (`analysis_expiration_extension_threshold_days`, `analysis_expiration_extension_days`).
 */
@Injectable({ providedIn: 'root' })
export class AnalysisExpirationService {
	private readonly config = inject(ConfigService);
	private readonly analysisApi = inject(AnalysisApiService);
	private readonly dialogs = inject(DialogService);
	private readonly logger = inject(LoggerService);

	private pending: Promise<AnalysisExpirationSettings> | null = null;

	readonly settings = signal(defaultAnalysisExpirationSettings());

	/**
	 * Loads remote config once per successful fetch. Failures keep the in-app
	 * defaults and can be retried.
	 */
	load(): Promise<AnalysisExpirationSettings> {
		if (!this.pending) {
			this.pending = this.fetchSettings()
				.then((settings) => {
					this.settings.set(settings);
					return settings;
				})
				.catch((error: unknown) => {
					this.pending = null;
					this.logger.captureException(
						error instanceof Error
							? error
							: new Error(String(error)),
						{ source: 'AnalysisExpirationService.load' },
					);
					return this.settings();
				});
		}

		return this.pending;
	}

	canExtend(expiresAt: ExpirationInstant, now: Date = new Date()): boolean {
		return isWithinExpirationExtensionWindow(
			expiresAt,
			this.settings().thresholdDays,
			now,
		);
	}

	/**
	 * Confirmation shared by the dashboard menu, the analysis menu, and the banner.
	 */
	prompt(analysisName: string) {
		return this.dialogs
			.open<boolean>(
				new PolymorpheusComponent(ConfirmDialogComponent),
				ConfirmDialogComponent.Options({
					label: 'analyses.dialog.extend-expiration.label',
					data: {
						description:
							'analyses.dialog.extend-expiration.description',
						acceptText: 'analyses.action.extend',
						cancelText: 'action.cancel',
						translationParams: {
							days: this.settings().extensionDays,
							analysis: analysisName,
						},
					},
				}),
			)
			.pipe(defaultIfEmpty(false));
	}

	/**
	 * Adds the configured number of days to the analysis expiration when it is
	 * still inside the extension window.
	 */
	async apply(analysisId: string): Promise<Analysis> {
		const settings = await this.load();
		const analysis = await this.analysisApi.findById(analysisId, false);
		if (
			!isWithinExpirationExtensionWindow(
				analysis.expiresAt,
				settings.thresholdDays,
			)
		) {
			throw new Error(
				'Analysis is outside the expiration extension window',
			);
		}

		const expiresAt = Timestamp.fromDate(
			extendExpirationDate(analysis.expiresAt, settings.extensionDays),
		);

		await this.analysisApi.update(
			{ id: analysisId, expiresAt },
			{ addActivityLog: true, type: 'update' },
		);

		return { ...analysis, expiresAt };
	}

	private async fetchSettings(): Promise<AnalysisExpirationSettings> {
		const defaults = defaultAnalysisExpirationSettings();
		const [thresholdDays, extensionDays] = await Promise.all([
			this.config.getNumber(
				'analysis_expiration_extension_threshold_days',
			),
			this.config.getNumber('analysis_expiration_extension_days'),
		]);

		return {
			thresholdDays: positiveDayCount(
				thresholdDays,
				defaults.thresholdDays,
			),
			extensionDays: positiveDayCount(
				extensionDays,
				defaults.extensionDays,
			),
		};
	}
}
